import { COMPRESSOR_FIELD_NAMES } from 'src/constants/compressorFields';
import { PERFORMANCE_INDICATORS } from 'src/constants/enpisFields';
import { RECORD_FIELD_NAMES } from 'src/constants/recordFields';
import Unit from 'src/constants/unit';
import { ENERGY_FIELD_NAMES } from 'src/constants/energyFields';

const Conversion = {
  FLOW_RATIO: 1.69901,
  FEET_PER_METER: 3.28084,
  FC_RATIO: 5 / 9,
  FC_MINUS: 32,
  PRESSURE_RATIO: 6.8947572932,
  POWER_RATIO: 0.98632,
  FT3_PER_M3: 35.3147,
  STORAGE_RATIO: 3.78541,
  POWER_RATIO_KW: 3412.14,
  COST_PER_UNIT: 0.0283,
  FLOW_M3H_TO_M3MIN: 60,
  PRESSURE_KPA_TO_BAR: 100,
};

function CFMtoCMH(value) {
  return value * Conversion.FLOW_RATIO;
}

function CMHToCFM(value) {
  return value / Conversion.FLOW_RATIO;
}

function CMHtoCMM(value) {
  return value / Conversion.FLOW_M3H_TO_M3MIN;
}

function CMMtoCMH(value) {
  return value * Conversion.FLOW_M3H_TO_M3MIN;
}

function KpaToBar(value) {
  return value / Conversion.PRESSURE_KPA_TO_BAR;
}

function BarToKpa(value) {
  return value * Conversion.PRESSURE_KPA_TO_BAR;
}

function FToC(value) {
  const temp = value - Conversion.FC_MINUS;
  return temp * Conversion.FC_RATIO;
}

function CToF(value) {
  const temp = value * (1 / Conversion.FC_RATIO);
  return temp + Conversion.FC_MINUS;
}

function PsiToKpa(value) {
  return value * Conversion.PRESSURE_RATIO;
}

function KpaToPsi(value) {
  return value / Conversion.PRESSURE_RATIO;
}

function FeetToMeter(value) {
  return value / Conversion.FEET_PER_METER;
}

function MeterToFeet(value) {
  return value * Conversion.FEET_PER_METER;
}

function MBHPToIBHP(value) {
  return value / Conversion.POWER_RATIO;
}

function IBHPToMBHP(value) {
  return value * Conversion.POWER_RATIO;
}

function CMDollarsToCFtDollars(value) {
  return value * Conversion.FT3_PER_M3;
}

function CftDollarstoCMDollars(value) {
  return value / Conversion.FT3_PER_M3;
}

function LitresToUSGallons(value) {
  return value * Conversion.STORAGE_RATIO;
}

function USGallonsToLitres(value) {
  return value / Conversion.STORAGE_RATIO;
}

function kWToBTUh(value) {
  return value * Conversion.POWER_RATIO_KW;
}

function BTUhToKW(value) {
  return value / Conversion.POWER_RATIO_KW;
}

function DollarPerCfToDollarPerM3(value) {
  return value / Conversion.COST_PER_UNIT;
}

function DollarPerM3ToDollarPerCf(value) {
  return value * Conversion.COST_PER_UNIT;
}

function convertUnit(columnName, value, unit) {
  switch (columnName) {
    case RECORD_FIELD_NAMES.FLOW:
    case RECORD_FIELD_NAMES.COMPRESSOR_FLOW:
    case COMPRESSOR_FIELD_NAMES.NAMEPLATE_CAPACITY:
    case COMPRESSOR_FIELD_NAMES.COMPRESSOR_FLOW_SEU:
    case COMPRESSOR_FIELD_NAMES.SYSTEM_MEASURED_FLOW:
    case `ENPI_${PERFORMANCE_INDICATORS.VOL_FLOW_FOUND}`:
    case `ENB_${PERFORMANCE_INDICATORS.VOL_FLOW_FOUND}`:
    case `ENPI_${PERFORMANCE_INDICATORS.VOL_FLOW_REPAIRED}`:
    case `ENB_${PERFORMANCE_INDICATORS.VOL_FLOW_REPAIRED}`:
      if (unit === Unit.IMPERIAL) return CMHToCFM(value);
      if (unit === Unit.METRIC_EU) return CMHtoCMM(value);
      return CFMtoCMH(value);

    case ENERGY_FIELD_NAMES.FE_EFFICIENCY:
      // Efficiency is kW per flow unit (kW/cfm or kW/m³/h or kW/m³/min).
      // Direction is INVERTED vs flow because efficiency is per-flow, not flow itself:
      //   metric (kW/m³/h)   → imperial (kW/cfm):    × 1.69901  (CFMtoCMH)
      //   imperial (kW/cfm)  → metric (kW/m³/h):    ÷ 1.69901  (CMHToCFM)
      //   metric (kW/m³/h)   → metric_eu (kW/m³/min): × 60      (CMMtoCMH, since kW/(m³/h) × 60 = kW/(m³/min))
      if (unit === Unit.IMPERIAL) return CFMtoCMH(value);
      if (unit === Unit.METRIC_EU) return CMMtoCMH(value);
      return CMHToCFM(value);

    case COMPRESSOR_FIELD_NAMES.ENERGY_COST_PER_UNIT:
      if (unit === Unit.METRIC_EU) return value;
      return unit === Unit.METRIC ? DollarPerCfToDollarPerM3(value) : DollarPerM3ToDollarPerCf(value);

    case RECORD_FIELD_NAMES.LEAK_TEMPERATURE:
    case RECORD_FIELD_NAMES.AMBIENT_TEMPERATURE:
      if (unit === Unit.METRIC_EU) return value;
      return unit === Unit.METRIC ? FToC(value) : CToF(value);

    case RECORD_FIELD_NAMES.LEAK_PRESSURE:
    case COMPRESSOR_FIELD_NAMES.RATED_PRESSURE:
    case COMPRESSOR_FIELD_NAMES.PRESSURE_SETPOINT:
    case COMPRESSOR_FIELD_NAMES.COMPRESSOR_SETPOINT_SEU:
    case COMPRESSOR_FIELD_NAMES.SYSTEM_PRESSURE_SETPOINT:
    case `ENPI_${PERFORMANCE_INDICATORS.SYSTEM_CONTROL_PRESSURE_REDUCED}`:
    case `ENB_${PERFORMANCE_INDICATORS.SYSTEM_CONTROL_PRESSURE_REDUCED}`:
      if (unit === Unit.IMPERIAL) return KpaToPsi(value);
      if (unit === Unit.METRIC_EU) return KpaToBar(value);
      return PsiToKpa(value);

    case COMPRESSOR_FIELD_NAMES.TOTAL_AIR_STORAGE_SIZE:
      if (unit === Unit.METRIC_EU) return value;
      return unit === Unit.METRIC ? LitresToUSGallons(value) : USGallonsToLitres(value);

    case `ENPI_${PERFORMANCE_INDICATORS.ENERGY_SAVING_OPPORTUNITY_FOUND}`:
    case `ENB_${PERFORMANCE_INDICATORS.ENERGY_SAVING_OPPORTUNITY_FOUND}`:
    case `ENPI_${PERFORMANCE_INDICATORS.ENERGY_SAVING_REALIZED_FIXED}`:
    case `ENB_${PERFORMANCE_INDICATORS.ENERGY_SAVING_REALIZED_FIXED}`:
    case COMPRESSOR_FIELD_NAMES.FULL_LOAD_INPUT_POWER:
      if (unit === Unit.METRIC_EU) return value;
      return unit === Unit.METRIC ? kWToBTUh(value) : BTUhToKW(value);

    case RECORD_FIELD_NAMES.DISTANCE:
    case RECORD_FIELD_NAMES.ALTITUDE:
      if (unit === Unit.METRIC_EU) return value;
      return unit === Unit.METRIC ? FeetToMeter(value) : MeterToFeet(value);

    case RECORD_FIELD_NAMES.COMPRESSOR_BHP:
      if (unit === Unit.METRIC_EU) return value;
      return unit === Unit.METRIC ? IBHPToMBHP(value) : MBHPToIBHP(value);

    case RECORD_FIELD_NAMES.GAS_COST:
      if (unit === Unit.METRIC_EU) return value;
      return unit === Unit.METRIC ? CMDollarsToCFtDollars(value) : CftDollarstoCMDollars(value);

    default:
      return value;
  }
}

export function convertToMetric(columnName, value, fromUnit) {
  if (fromUnit === Unit.METRIC) return value;
  if (fromUnit === Unit.IMPERIAL) return convertUnit(columnName, value, Unit.METRIC);
  // fromUnit === Unit.METRIC_EU
  switch (columnName) {
    case RECORD_FIELD_NAMES.FLOW:
    case RECORD_FIELD_NAMES.COMPRESSOR_FLOW:
    case COMPRESSOR_FIELD_NAMES.NAMEPLATE_CAPACITY:
    case COMPRESSOR_FIELD_NAMES.COMPRESSOR_FLOW_SEU:
    case COMPRESSOR_FIELD_NAMES.SYSTEM_MEASURED_FLOW:
    case `ENPI_${PERFORMANCE_INDICATORS.VOL_FLOW_FOUND}`:
    case `ENB_${PERFORMANCE_INDICATORS.VOL_FLOW_FOUND}`:
    case `ENPI_${PERFORMANCE_INDICATORS.VOL_FLOW_REPAIRED}`:
    case `ENB_${PERFORMANCE_INDICATORS.VOL_FLOW_REPAIRED}`:
      return CMMtoCMH(value);
    case ENERGY_FIELD_NAMES.FE_EFFICIENCY:
      return CMHtoCMM(value);
    case RECORD_FIELD_NAMES.LEAK_PRESSURE:
    case COMPRESSOR_FIELD_NAMES.RATED_PRESSURE:
    case COMPRESSOR_FIELD_NAMES.PRESSURE_SETPOINT:
    case COMPRESSOR_FIELD_NAMES.COMPRESSOR_SETPOINT_SEU:
    case COMPRESSOR_FIELD_NAMES.SYSTEM_PRESSURE_SETPOINT:
    case `ENPI_${PERFORMANCE_INDICATORS.SYSTEM_CONTROL_PRESSURE_REDUCED}`:
    case `ENB_${PERFORMANCE_INDICATORS.SYSTEM_CONTROL_PRESSURE_REDUCED}`:
      return BarToKpa(value);
    default:
      return value;
  }
}

export function centsToDollars(value) {
  const cents = parseInt(value, 10);
  return !Number.isNaN(cents) ? Number(cents / 100) : 0.0;
}

export function dollarsToCents(value) {
  // use .toFixed(2) for 'truncate' non meaningful digits
  const cents = Number(value).toFixed(2) * 100;
  return !Number.isNaN(cents) && Number.isFinite(cents) ? cents : 0;
}

export default convertUnit;
